import { useTranslation } from 'react-i18next';
import type { ProjectCustomDomainInfo } from '@claude-code-api/shared';
import {
  CheckCircle2,
  CircleDotDashed,
  KeyRound,
  Route,
  Server,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';
import { Badge } from '@/components/ui';

type HealthTone = 'success' | 'warning' | 'destructive' | 'pending';

interface HealthItem {
  key: 'dns' | 'session' | 'routing' | 'certificate';
  tone: HealthTone;
  statusKey: string;
  detail: string;
  icon: typeof CheckCircle2;
}

interface ProjectCustomDomainHealthProps {
  domain: ProjectCustomDomainInfo;
}

function formatTimestamp(value: string | null, locale: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function ProjectCustomDomainHealth({
  domain,
}: ProjectCustomDomainHealthProps) {
  const { t, i18n } = useTranslation('projects');
  const checkedAt = formatTimestamp(domain.lastCheckedAt, i18n.language);
  const reconciledAt = formatTimestamp(domain.lastReconciledAt, i18n.language);
  const certificateExpiry = formatTimestamp(domain.certificateExpiresAt, i18n.language);
  const isLocal = domain.setupRecord.type === 'HOSTS';

  const dnsTone: HealthTone = domain.status === 'active'
    ? 'success'
    : domain.status === 'suspended'
      ? 'destructive'
      : 'warning';
  const sessionTone: HealthTone = domain.targetStatus === 'running'
    ? 'success'
    : domain.targetStatus === 'missing'
      ? 'destructive'
      : 'warning';
  const routingTone: HealthTone = domain.reconciliationError
    ? 'destructive'
    : domain.status === 'active' || domain.status === 'degraded'
      ? domain.targetStatus === 'running' ? 'success' : 'warning'
      : 'pending';
  const certificateTone: HealthTone = isLocal || domain.certificateStatus === 'valid'
    ? 'success'
    : domain.certificateStatus === 'expired' || domain.certificateStatus === 'error'
      ? 'destructive'
      : domain.certificateStatus === 'expiring'
        ? 'warning'
        : 'pending';

  const items: HealthItem[] = [
    {
      key: 'dns',
      tone: dnsTone,
      statusKey: domain.status,
      detail: domain.lastError
        ?? (checkedAt
          ? t('deployments.customDomain.health.checkedAt', { value: checkedAt })
          : t('deployments.customDomain.health.notChecked')),
      icon: KeyRound,
    },
    {
      key: 'session',
      tone: sessionTone,
      statusKey: domain.targetStatus,
      detail: t(`deployments.customDomain.targetStatus.${domain.targetStatus}.description`),
      icon: Server,
    },
    {
      key: 'routing',
      tone: routingTone,
      statusKey: domain.reconciliationError
        ? 'error'
        : domain.status === 'suspended' || domain.status === 'deleting'
          ? 'inactive'
          : domain.targetStatus === 'running' ? 'live' : 'offline',
      detail: domain.reconciliationError
        ?? (reconciledAt
          ? t('deployments.customDomain.health.reconciledAt', { value: reconciledAt })
          : t('deployments.customDomain.health.notReconciled')),
      icon: Route,
    },
    {
      key: 'certificate',
      tone: certificateTone,
      statusKey: isLocal ? 'local' : domain.certificateStatus,
      detail: domain.certificateError
        ?? (certificateExpiry
          ? t('deployments.customDomain.health.expiresAt', { value: certificateExpiry })
          : isLocal
            ? t('deployments.customDomain.health.localCertificate')
            : t('deployments.customDomain.health.certificatePending')),
      icon: ShieldCheck,
    },
  ];

  const iconByTone = {
    success: CheckCircle2,
    warning: TriangleAlert,
    destructive: TriangleAlert,
    pending: CircleDotDashed,
  } as const;

  return (
    <section className="space-y-3" aria-labelledby="custom-domain-health-title" aria-live="polite">
      <div>
        <h3 id="custom-domain-health-title" className="text-sm font-semibold">
          {t('deployments.customDomain.health.title')}
        </h3>
        <p className="text-xs text-muted-foreground">
          {t('deployments.customDomain.health.description')}
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border bg-background/70">
        <div className="grid md:grid-cols-2">
          {items.map((item, index) => {
            const StateIcon = iconByTone[item.tone];
            const ItemIcon = item.icon;
            const borderClass = index === 0
              ? 'border-b md:border-r'
              : index === 1
                ? 'border-b'
                : index === 2
                  ? 'border-b md:border-b-0 md:border-r'
                  : '';
            return (
              <div key={item.key} className={`min-w-0 p-4 ${borderClass}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <ItemIcon className="h-4 w-4 text-muted-foreground" />
                    {t(`deployments.customDomain.health.${item.key}.label`)}
                  </div>
                  <Badge variant={item.tone} className="max-w-full gap-1">
                    <StateIcon className="h-3 w-3" />
                    {t(`deployments.customDomain.health.${item.key}.status.${item.statusKey}`)}
                  </Badge>
                </div>
                <p className="mt-2 break-words text-xs leading-relaxed text-muted-foreground">
                  {item.detail}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
